import React, { useEffect, useState, useMemo } from 'react';
import { Table, Card, Button, Space, Tag, Typography, Input, Popconfirm, message, Select, Row, Col } from 'antd';
import { SearchOutlined, DeleteOutlined, ReloadOutlined, DollarOutlined, CheckCircleOutlined, CloseCircleOutlined, WalletOutlined } from '@ant-design/icons';
import StatCard from '../components/StatCard';
import usePaymentStore from '../store/paymentStore';

const { Title } = Typography;

const statusColors = {
  initiated: 'default',
  paid: 'green',
  failed: 'red',
  refunded: 'purple',
};

const statusLabels = {
  initiated: 'Boshlangan',
  paid: "To'langan",
  failed: 'Muvaffaqiyatsiz',
  refunded: 'Qaytarilgan',
};

const PaymentsPage = () => {
  const { payments, loading, pagination, fetchPayments, deletePayment } = usePaymentStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleDelete = async (id) => {
    try {
      await deletePayment(id);
      message.success("O'chirildi");
      fetchPayments();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60, sorter: (a, b) => a.id - b.id },
    { title: 'Buyurtma ID', dataIndex: 'booking_id', key: 'booking_id' },
    {
      title: 'Summa',
      dataIndex: 'amount',
      key: 'amount',
      render: (v, r) => `${Number(v || 0).toLocaleString()} ${r.currency || 'UZS'}`,
      sorter: (a, b) => a.amount - b.amount,
    },
    { title: 'Usul', dataIndex: 'method', key: 'method', render: (v) => v || '-' },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (s) => <Tag color={statusColors[s]}>{statusLabels[s] || s}</Tag>,
    },
    { title: 'Tranzaksiya', dataIndex: 'transaction_ref', key: 'transaction_ref', ellipsis: true, render: (v) => v || '-' },
    {
      title: 'Sana',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (d) => d ? new Date(d).toLocaleDateString('uz-UZ') : '-',
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Popconfirm title="O'chirishni tasdiqlaysizmi?" onConfirm={() => handleDelete(record.id)}>
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const filtered = payments?.filter((p) => {
    const matchSearch = !search || String(p.id).includes(search) || String(p.booking_id).includes(search) || (p.transaction_ref || '').includes(search);
    const matchStatus = !statusFilter || p.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const stats = useMemo(() => {
    const list = payments || [];
    return {
      total: list.length,
      paid: list.filter(p => p.status === 'paid').length,
      failed: list.filter(p => p.status === 'failed').length,
      totalAmount: list.filter(p => p.status === 'paid').reduce((s, p) => s + Number(p.amount || 0), 0),
    };
  }, [payments]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Title level={3} style={{ margin: 0 }}>To'lovlar</Title>
        <Space wrap>
          <Input placeholder="Qidirish..." prefix={<SearchOutlined />} value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 200 }} allowClear />
          <Select
            placeholder="Holat"
            allowClear
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 160 }}
            options={Object.entries(statusLabels).map(([k, v]) => ({ value: k, label: v }))}
          />
          <Button icon={<ReloadOutlined />} onClick={() => fetchPayments()}>Yangilash</Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Jami to'lovlar" value={stats.total} icon={<WalletOutlined />} color="#4f46e5" loading={loading} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="To'langan" value={stats.paid} icon={<CheckCircleOutlined />} color="#52c41a" loading={loading} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Muvaffaqiyatsiz" value={stats.failed} icon={<CloseCircleOutlined />} color="#ff4d4f" loading={loading} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Jami summa" value={stats.totalAmount} suffix="UZS" icon={<DollarOutlined />} color="#13c2c2" loading={loading} />
        </Col>
      </Row>

      <Card style={{ borderRadius: 12 }}>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 900 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Jami: ${total}`,
          }}
          onChange={(pg) => fetchPayments({ page: pg.current, per_page: pg.pageSize })}
        />
      </Card>
    </div>
  );
};

export default PaymentsPage;
